import { ChevronDown } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { Department, Category, ThemeColors } from '../../types';

interface MobileMenuProps {
  isOpen: boolean;
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  departments: Department[];
  categories: Category[];
  selectedDepartmentId: number | null;
  selectedCategoryId: number | null;
  onDepartmentClick: (departmentId: number | null) => void;
  onCategoryClick: (categoryId: number, departmentId: number) => void;
  onAllProductsClick: () => void;
  themeColors?: ThemeColors;
}

export function MobileMenu({
  isOpen,
  searchQuery,
  onSearchChange,
  departments,
  selectedDepartmentId,
  selectedCategoryId,
  onDepartmentClick,
  onCategoryClick,
  onAllProductsClick,
  themeColors,
}: MobileMenuProps) {
  const [expandedDeptId, setExpandedDeptId] = useState<number | null>(selectedDepartmentId);
  const contentRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [, forceUpdate] = useState(0);
  const [animReady, setAnimReady] = useState(false);

  useLayoutEffect(() => {
    // Step 1: re-render with correct heights but NO transitions (snap to initial state)
    forceUpdate(n => n + 1);
    // Step 2: after browser paints the correct state, enable transitions for future interactions
    const raf = requestAnimationFrame(() => setAnimReady(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const toggleDept = (deptId: number) => {
    setExpandedDeptId(prev => prev === deptId ? null : deptId);
  };

  const activeColor = themeColors?.left_side_active_color || '#ffffff';
  const itemColor = themeColors?.left_side_color || '#e5e7eb';
  const bgColor = themeColors?.left_side_background || '#1f2937';
  const headingColor = themeColors?.left_side_heading_color || themeColors?.left_side_color || '#9ca3af';

  const isAllActive = selectedDepartmentId === null && selectedCategoryId === null;

  return (
    <div
      className="md:hidden fixed left-0 right-0 bottom-0 z-30 overflow-y-auto"
      style={{
        top: 'var(--header-height, 64px)',
        backgroundColor: bgColor,
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 340ms cubic-bezier(0.4, 0, 0.2, 1)',
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
    >
      <nav className="px-3 py-3" aria-label="Mobile navigation">
        {/* All Departments */}
        <button
          onClick={onAllProductsClick}
          className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 outline-none focus:outline-none focus-visible:outline-none mb-1"
          style={{
            color: isAllActive ? activeColor : itemColor,
            backgroundColor: isAllActive ? `color-mix(in srgb, ${activeColor} 20%, transparent)` : undefined,
            fontWeight: isAllActive ? 600 : 400,
          }}
          onTouchStart={e => { if (!isAllActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
          onTouchEnd={e => { if (!isAllActive) e.currentTarget.style.backgroundColor = ''; }}
          aria-current={isAllActive ? 'page' : undefined}
        >
          All Departments
        </button>

        <div className="h-px bg-white/10 my-2" />

        {/* Department list with accordion */}
        <p className="px-3 mb-1.5 text-xs font-semibold uppercase tracking-widest opacity-50" style={{ color: headingColor }}>
          Departments
        </p>

        {departments.map(dept => {
          const isDeptActive = Number(selectedDepartmentId) === Number(dept.id);
          const isExpanded = expandedDeptId === dept.id;
          const categoryCount = dept.productCategories?.length ?? 0;
          const hasCategories = categoryCount > 0;
          const hasAccordion = categoryCount > 1;

          // Real pixel height for smooth balanced animation
          const contentEl = contentRefs.current.get(dept.id);
          const expandedHeight = contentEl ? contentEl.scrollHeight : 0;

          return (
            <div key={dept.id}>
              <div className="flex items-center rounded-lg overflow-hidden"
                style={{
                  backgroundColor: isDeptActive ? `color-mix(in srgb, ${activeColor} 20%, transparent)` : undefined,
                }}>
                {/* Dept name — navigates if single category, else toggles accordion */}
                <button
                  onClick={() => hasAccordion ? toggleDept(dept.id) : onDepartmentClick(dept.id)}
                  className="flex-1 text-left px-3 py-2.5 text-sm transition-all duration-150 outline-none focus:outline-none focus-visible:outline-none"
                  style={{
                    color: isDeptActive ? activeColor : itemColor,
                    fontWeight: isDeptActive ? 600 : 400,
                  }}
                >
                  {dept.name}{dept.memo ? ` (${dept.memo})` : ''}
                </button>

                {/* Chevron — only for multi-category departments */}
                {hasAccordion && (
                  <button
                    onClick={() => toggleDept(dept.id)}
                    className="px-3 py-2.5 outline-none focus:outline-none focus-visible:outline-none hover:bg-white/10 flex-shrink-0"
                    style={{ color: isDeptActive ? activeColor : itemColor }}
                    aria-label={isExpanded ? `Collapse ${dept.name}` : `Expand ${dept.name}`}
                    aria-expanded={isExpanded}
                  >
                    <ChevronDown
                      className="w-4 h-4"
                      style={{
                        opacity: isExpanded ? 0.6 : 0.4,
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: animReady ? 'transform 480ms cubic-bezier(0.4, 0, 0.2, 1), opacity 480ms ease' : 'none',
                      }}
                    />
                  </button>
                )}
              </div>

              {/* Categories — always in DOM, height animated via real scrollHeight */}
              <div
                style={{
                  height: isExpanded && hasAccordion ? expandedHeight : 0,
                  opacity: isExpanded && hasAccordion ? 1 : 0,
                  overflow: 'hidden',
                  transition: animReady ? 'height 480ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms ease' : 'none',
                }}
              >
                <div
                  ref={el => { if (el) contentRefs.current.set(dept.id, el); else contentRefs.current.delete(dept.id); }}
                  className="ml-3 pl-3 border-l border-white/15 mt-0.5 mb-1 space-y-0.5"
                >
                  {/* All in dept — visually distinct */}
                  <div className="pb-1 mb-0.5" style={{ borderBottom: `1px solid ${itemColor}20` }}>
                    <button
                      onClick={() => { onDepartmentClick(dept.id); }}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 outline-none focus:outline-none focus-visible:outline-none"
                      style={{
                        color: isDeptActive && !selectedCategoryId ? activeColor : itemColor,
                        backgroundColor: isDeptActive && !selectedCategoryId ? `color-mix(in srgb, ${activeColor} 15%, transparent)` : undefined,
                        fontWeight: isDeptActive && !selectedCategoryId ? 700 : 500,
                      }}
                      onTouchStart={e => { if (!(isDeptActive && !selectedCategoryId)) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
                      onTouchEnd={e => { if (!(isDeptActive && !selectedCategoryId)) e.currentTarget.style.backgroundColor = ''; }}
                    >
                      All categories
                    </button>
                  </div>

                  {dept.productCategories!.map(cat => {
                    const isCatActive = isDeptActive && selectedCategoryId === cat.id;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => onCategoryClick(cat.id, dept.id)}
                        className="w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-150 outline-none focus:outline-none focus-visible:outline-none"
                        style={{
                          color: isCatActive ? activeColor : itemColor,
                          backgroundColor: isCatActive ? `color-mix(in srgb, ${activeColor} 15%, transparent)` : undefined,
                          fontWeight: isCatActive ? 600 : 400,
                        }}
                        aria-current={isCatActive ? 'page' : undefined}
                      >
                        {cat.name}{cat.memo ? ` (${cat.memo})` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </nav>
    </div>
  );
}
